import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import { CalendarUndatedDraftDragSource } from '~publish/components/Calendar/CalendarUndatedDraftDragSource'
import { CalendarUndatedDraftMiniTrigger } from '~publish/components/Calendar/CalendarUndatedDraftsPanel'
import { ContentItemDetailsCard } from '~publish/components/ContentItemCard'
import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import { CalendarUndatedDraftCard } from '~publish/components/Calendar/CalendarUndatedDraftCard'
import { isOfType } from '~publish/helpers/typeGuards'

import { UNDATED_CONTENT_ITEM_DRAG_SOURCE } from './contentItemDrag'
import type { UndatedContentItemDragData } from './contentItemDrag'
import { ContentItemPopover } from './ContentItemPopover'

const LABEL_MAX_LENGTH = 60

// Draft text is unbounded; truncate before it reaches a screen-reader label.
const labelSnippet = (text: string): string => {
  const trimmed = text.trim()
  return trimmed.length > LABEL_MAX_LENGTH
    ? `${trimmed.slice(0, LABEL_MAX_LENGTH)}…`
    : trimmed
}

const previewLabel = (text: string): string => {
  const snippet = labelSnippet(text)
  return snippet
    ? `Preview undated draft "${snippet}"`
    : 'Preview undated draft'
}

/**
 * Compact No Date sidebar card for a channel-less draft. Draggable onto a
 * calendar cell to set its `targetDate` (see useHandleDragEnd), mirroring the
 * post minis; clicking the card opens the preview instead.
 *
 * @see ContentItemPopover for the click-to-preview and its actions.
 */
export function CalendarUndatedContentItemDraft({
  contentItem,
  isDragDisabled,
  isSidebarOpen,
}: {
  contentItem: ContentItemDraft
  isDragDisabled: boolean
  isSidebarOpen: boolean
}): JSX.Element | null {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const body = isOfType(contentItem.body, 'DraftContent')
    ? contentItem.body
    : null

  const dragData: UndatedContentItemDragData = {
    type: 'contentItem',
    source: UNDATED_CONTENT_ITEM_DRAG_SOURCE,
    contentItem,
    label: `undated draft "${labelSnippet(body?.text ?? '')}"`,
  }

  useEffect(() => {
    if (!isSidebarOpen) setIsPreviewOpen(false)
  }, [isSidebarOpen])

  if (!body) return null

  return (
    <CalendarUndatedDraftDragSource
      id={`undated-ci:${contentItem.id}`}
      data={dragData}
      disabled={isDragDisabled}
      dragLabel={`Drag undated draft "${labelSnippet(body.text)}" to schedule`}
    >
      <ContentItemPopover
        preview={<ContentItemDetailsCard contentItem={contentItem} />}
        isOpen={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        side="left"
      >
        <CalendarUndatedDraftMiniTrigger aria-label={previewLabel(body.text)}>
          <CalendarUndatedDraftCard text={body.text} />
        </CalendarUndatedDraftMiniTrigger>
      </ContentItemPopover>
    </CalendarUndatedDraftDragSource>
  )
}
